import { z } from 'zod'

/**
 * A List is a titled, ordered set of tickable items (shopping, packing,
 * routines). Items change only through actions (see apply.ts), never by
 * patching `items`, so two devices working the same list merge.
 */
export const listItem = z
  .object({
    id: z.string().min(1).max(64),
    text: z.string().min(1).max(500),
    done: z.boolean().default(false),
    /** who put it there (a capture's from: label); empty = the owner */
    added_by: z.string().max(64).optional(),
    /** when it was last ticked, RFC 3339 UTC */
    done_at: z.string().optional(),
    /** an optional reminder for this item, RFC 3339 UTC; fires unless the item is ticked */
    remind_at: z.string().optional(),
  })
  .strict()
export type ListItem = z.infer<typeof listItem>

export const LIST_STYLES = ['checklist', 'reusable'] as const
export type ListStyle = (typeof LIST_STYLES)[number]

export const listSpec = z
  .object({
    title: z.string().min(1).max(200),
    icon: z.string().max(40).optional(),
    /** checklist: ticked items get cleared away; reusable: ticked items reset */
    style: z.enum(LIST_STYLES).default('checklist'),
    items: z.array(listItem).max(500).default([]),
    pinned: z.boolean().default(false),
    /** one-off reminder, RFC 3339 UTC */
    remind_at: z.string().optional(),
  })
  .strict()
export type ListSpec = z.infer<typeof listSpec>

export const listStatus = z.object({
  last_touched: z.string(),
  reminder_fired_for: z.string().optional(),
  /** what this list became (tasks, project, page); it is then retired */
  converted_to: z.object({ kind: z.string(), id: z.string() }).strict().optional(),
  /** item id → the remind_at already sent */
  item_reminders_fired: z.record(z.string(), z.string()).optional(),
})
export type ListStatus = z.infer<typeof listStatus>

export const MAX_ITEMS = 500
